// Montage du parcours « Compose ton lot » ([funnel_bundle_wizard ids="..."]).
// Charge les lots (un par taille), construit les données du parcours et branche l'ajout au panier.
import wizardCss from './wizard.css?inline'
import { runWizard } from './wizard.js'
import { detectLang, makeI18n } from './i18n.js'
import type { FunnelConfig, ConfigProduct } from '@/lib/hooks/useFunnelConfig'
import { siteHeaderHeight, watchSiteHeader } from '@/lib/siteHeader'

// Données WooCommerce fournies par le plugin : dimensions (par slug), photos et stock (par ID WooCommerce)
interface WcData {
  dims?: Record<string, string>
  photos?: Record<string, string>
  stock?: Record<string, boolean> // false = rupture de stock
  notes?: Record<string, string> // note par slug de lot (shortcode notes="slug=texte"), ex. précommande
  names?: Record<string, string> // titres WooCommerce dans la langue de la page (par ID), via TrueLang ; absent en français
  ratio?: number // largeur / hauteur des calques, mesurée par le plugin (0 ou absent : mesurée ici)
}

interface MountOptions {
  slugs: string[]
  apiUrl: string
  wcAjaxUrl?: string
  wcNonce?: string
  wcData: WcData
  funnels?: Record<string, unknown> // réglages des lots écrits dans la page par le plugin (par slug)
  urls: { checkout: string; shop: string }
  lang?: string // code TrueLang de la page (nl, en-us, es, it…), vide en français
}

type I18n = ReturnType<typeof makeI18n>

// Visuel de chaque catégorie d'accessoires, choisi par Theo : pas le fermoir ovale métal (déjà inclus),
// pas l'ovale rouge perforé (pire vente), et la sangle large plutôt que la fine (déjà incluse).
const CATEGORY_COVERS: Record<string, string> = {
  'Fermoir non perforé': 'Coeur noir',
  'Fermoir perforé': 'Coeur noir - perforé',
  Sangles: 'Bandoulière confort réglable',
}
// Ordre des catégories d'accessoires : portefeuille en premier
const CATEGORY_FIRST = ['Portefeuille']

type WizardExtraProduct = { id: string; name: string; trackName: string; price: number; thumb: string; wcId?: number; included: boolean }

// Nom affiché, dans l'ordre : nom du configurateur traduit par le dictionnaire (noms choisis par Theo),
// titre WooCommerce traduit par TrueLang, nom français du configurateur.
// Les rabats perdent leur préfixe (« Rabat - Tweed rouge » → « Tweed rouge », « Klep – Rode tweed » → « Rode tweed »).
function nameOf(p: { name: string; wcId?: number }, wc: WcData, i18n: I18n, stripPrefix = false): string {
  const fr = (stripPrefix ? p.name.replace(/^Rabat\s*-\s*/i, '') : p.name).trim()
  if (i18n.hasTerm(fr)) return i18n.term(fr)
  const tr = p.wcId ? wc.names?.[String(p.wcId)] : undefined
  if (!tr) return fr
  return stripPrefix ? tr.replace(/^[^-–]+\s[-–]\s*/, '').trim() : tr
}

function photoOf(p: ConfigProduct, wc: WcData): string | undefined {
  return p.wcId ? wc.photos?.[String(p.wcId)] : undefined
}

function inStock(p: ConfigProduct, wc: WcData): boolean {
  return !p.wcId || wc.stock?.[String(p.wcId)] !== false
}

function buildSize(cfg: FunnelConfig, wc: WcData, i18n: I18n) {
  const note = i18n.term(wc.notes?.[cfg.slug] || '')
  const bc = cfg.bundleConfig!
  const flapCatId = bc.slots[0].sourceCategoryId
  const byId = new Map<string, { p: ConfigProduct; catName: string }>()
  for (const c of cfg.categories) for (const p of c.products) byId.set(p.id, { p, catName: c.name })

  const imposed = Object.entries(bc.baseLayerPicks)
    .filter(([catId]) => catId !== flapCatId)
    .map(([, pid]) => byId.get(pid))
    .filter((x): x is { p: ConfigProduct; catName: string } => !!x)
    .map(({ p, catName }) => ({ name: nameOf(p, wc, i18n), cat: catName, price: p.price, layer: p.layer, image: p.image, wcId: p.wcId,
      thumb: photoOf(p, wc) || p.thumb || p.image })) // vignette « Inclus : » (même photo que les cartes d'accessoires)

  const flapCat = cfg.categories.find(c => c.id === flapCatId)
  const flaps = (flapCat?.products || []).map(p => ({
    id: p.id,
    name: nameOf(p, wc, i18n, true),
    trackName: p.name.replace(/^Rabat\s*-\s*/i, '').trim(), // suivi : nom français, quelle que soit la langue
    price: p.price,
    layer: p.layer,
    image: p.image,
    thumb: p.thumb || p.image,
    photo: photoOf(p, wc),
    wcId: p.wcId,
    inStock: inStock(p, wc), // épuisé : affiché grisé, non sélectionnable
  }))

  // Accessoires : toutes les autres catégories du lot, sans les produits à 0 € (ex. anses incluses du cabas)
  // ni ceux en rupture de stock. Ceux déjà fournis avec le sac (sangle fine, fermoir ovale métal) restent
  // affichés mais grisés : des clientes reprenaient la sangle fine en accessoire sans savoir qu'elle était incluse.
  const includedIds = new Set(Object.values(bc.baseLayerPicks))
  const extras = cfg.categories
    .filter(c => c.id !== flapCatId)
    .map(c => {
      const products: WizardExtraProduct[] = c.products
        .filter(p => p.price > 0 && inStock(p, wc))
        .map(p => ({ id: p.id, name: nameOf(p, wc, i18n), trackName: p.name, price: p.price, thumb: photoOf(p, wc) || p.thumb || p.image, wcId: p.wcId, included: includedIds.has(p.id) }))
      const cover = products.find(p => p.trackName === CATEGORY_COVERS[c.name]) || products[0]
      return { id: c.id, name: i18n.term(c.name), trackName: c.name, products, cover: cover?.id }
    })
    .filter(c => c.products.length)
    .sort((a, b) => Number(CATEGORY_FIRST.includes(b.trackName)) - Number(CATEGORY_FIRST.includes(a.trackName)))

  return {
    key: cfg.size,
    slug: cfg.slug,
    lotName: cfg.name,
    label: nameOf({ name: cfg.baseName || cfg.name, wcId: cfg.baseWcId || undefined }, wc, i18n),
    trackLabel: cfg.baseName || cfg.name, // suivi : nom français
    note,
    price: cfg.basePrice || 0,
    baseWcId: cfg.baseWcId || null,
    body: cfg.baseImage,
    imposed,
    defaultFlap: bc.baseLayerPicks[flapCatId],
    flapCatId,
    slots: bc.slots,
    discount: bc.discountPercent,
    flaps,
    extras,
  }
}

type WizardSize = ReturnType<typeof buildSize>

function loadImageRatio(src: string): Promise<number> {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve(img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 520 / 308)
    img.onerror = () => resolve(520 / 308)
    img.src = src
  })
}

export async function mountBundleWizard(host: HTMLElement, opts: MountOptions) {
  const i18n = makeI18n(detectLang(opts.lang))
  const { t } = i18n
  const root = host.attachShadow({ mode: 'open' })
  // lang : sert à la césure des mots longs (« Umhänge-riemen » en allemand), voir .nm dans wizard.css
  root.innerHTML = `<style>${wizardCss}</style><div class="wz" lang="${i18n.lang}"><div class="wz-loading">${t('loading')}</div></div>`
  const wz = root.querySelector('.wz') as HTMLElement

  let configs: FunnelConfig[]
  try {
    const base = opts.apiUrl.replace(/\/$/, '')
    configs = await Promise.all(opts.slugs.map(async slug => {
      // Réglages déjà écrits dans la page par le plugin (cache 5 min) ; sinon, l'API comme avant
      const inline = opts.funnels?.[slug] as FunnelConfig | undefined
      if (inline && inline.slug) return inline
      const res = await fetch(`${base}/api/funnel/${encodeURIComponent(slug)}`)
      if (!res.ok) throw new Error(`Lot introuvable : ${slug}`)
      return res.json()
    }))
  } catch (err) {
    console.error('[FunnelWizard]', err)
    wz.innerHTML = `<div class="wz-loading">${t('loadError')}</div>`
    return
  }

  const sizes = configs.filter(c => c.type === 'bundle' && c.bundleConfig?.slots?.length).map(c => buildSize(c, opts.wcData, i18n))
  if (!sizes.length) {
    wz.innerHTML = `<div class="wz-loading">${t('noLot')}</div>`
    return
  }

  const dims: Record<string, string> = {}
  for (const sz of sizes) if (opts.wcData.dims?.[sz.slug]) dims[sz.key] = opts.wcData.dims[sz.slug]

  const DATA = {
    i18n,
    urls: opts.urls,
    discount: sizes[0].discount,
    sizes,
    dims,
    // Format des images de calques (toutes au même format), pour cadrer le sac sans le déformer
    // calculé par le plugin quand il le peut : pas d'attente du téléchargement de la photo avant d'afficher
    canvasRatio: opts.wcData.ratio || await loadImageRatio(sizes[0].body),
  }

  wz.innerHTML = `
    <div class="topline" id="topline" hidden></div>
    <div class="shell">
      <aside class="preview" aria-label="${t('ariaPreview')}">
        <div class="stage" id="stage"></div>
        <div class="stage-caption" id="caption"></div>
        <div class="trio" id="trio"></div>
        <div class="desk-sum" id="desksum"></div>
      </aside>
      <section class="panel" id="panel" aria-live="polite"></section>
    </div>
    <nav class="bar" id="bar" aria-label="${t('ariaNav')}" hidden></nav>`

  // Boutons flottants du site en bas de l'écran (sélecteur de langue TrueLang, bulle du mini-panier) :
  // remontés au-dessus du parcours, sinon ils recouvrent « Suivant » et « Précédent »
  if (!document.getElementById('fc-wizard-floating')) {
    const st = document.createElement('style')
    st.id = 'fc-wizard-floating'
    st.textContent = `#truelang-switcher, .xoo-wsc-basket { bottom: calc(var(--fc-wizard-bottom, 0px) + 12px) !important; transition: bottom .2s ease; }`
    document.head.appendChild(st)
  }

  // Envoi vers Google Analytics (gtag) et le pixel Meta (fbq) déjà chargés par le site ; silencieux s'ils sont absents
  type Tracker = (...args: unknown[]) => void
  const w = window as unknown as { gtag?: Tracker; fbq?: Tracker }
  const send = (ga: [string, Record<string, unknown>] | null, meta: [string, string, Record<string, unknown>] | null) => {
    try { if (ga) w.gtag?.('event', ...ga) } catch { /* suivi indisponible */ }
    try { if (meta) w.fbq?.(...meta) } catch { /* suivi indisponible */ }
  }

  const hooks = {
    track(name: string, params: Record<string, unknown>) {
      send([name, params], ['trackCustom', name, params])
    },
    // Ajout au panier : événements standard (montant en euros) + événement du parcours
    trackAddToCart(l: { value: number; lot: string; slug: string; taille: string; rabats: string; accessoires: number }) {
      const value = Math.round(l.value * 100) / 100
      send(['add_to_cart', { currency: 'EUR', value, items: [{ item_id: l.slug, item_name: l.lot, item_category: 'Lot', item_variant: l.taille, price: value, quantity: 1 }] }],
        ['track', 'AddToCart', { currency: 'EUR', value, content_name: l.lot, content_type: 'product', content_ids: [l.slug] }])
      send(['lot_ajout_panier', { valeur: value, taille: l.taille, rabats: l.rabats, accessoires: l.accessoires }],
        ['trackCustom', 'lot_ajout_panier', { value, currency: 'EUR', taille: l.taille, rabats: l.rabats, accessoires: l.accessoires }])
    },
    bottomInset(px: number) {
      document.documentElement.style.setProperty('--fc-wizard-bottom', px + 'px')
    },
    headerH: siteHeaderHeight,
    // Chaque étape s'ouvre en haut du parcours, juste sous l'en-tête du site
    scrollTop() {
      const y = host.getBoundingClientRect().top + window.scrollY - siteHeaderHeight()
      window.scrollTo({ top: Math.max(0, y) })
    },
    addToCart(
      S: { flaps: (string | null)[]; cart?: string },
      sz: WizardSize,
      extras: { catId: string; productId: string }[],
      rerender: () => void,
      onAdded: () => void,
    ) {
      const flaps = S.flaps.map(id => sz.flaps.find(f => f.id === id))
      if (flaps.some(f => !f?.wcId)) return
      if (!opts.wcAjaxUrl || !opts.wcNonce) {
        console.log('[FunnelWizard] Panier indisponible (hors WordPress)', { sz: sz.slug, flaps, extras })
        return
      }
      const fd = new FormData()
      fd.append('action', 'fc_add_bundle_to_cart')
      fd.append('nonce', opts.wcNonce)
      fd.append('bundle_slug', sz.slug)
      fd.append('bundle_name', sz.lotName)
      // Le plugin recalcule le sac depuis la config ; on envoie quand même la composition
      ;[sz.baseWcId, ...sz.imposed.map(p => p.wcId), flaps[0]!.wcId].forEach(id => id && fd.append('base_wc_ids[]', String(id)))
      // 1er rabat : celui monté sur le sac (remplace le rabat par défaut du lot)
      fd.append('base_overrides[0][category_id]', sz.flapCatId)
      fd.append('base_overrides[0][product_wc_id]', String(flaps[0]!.wcId))
      // 2e et 3e rabats : les emplacements du lot
      sz.slots.slice(0, 2).forEach((slot, i) => {
        fd.append(`slot_picks[${i}][slot_id]`, slot.id)
        fd.append(`slot_picks[${i}][slot_name]`, slot.name)
        fd.append(`slot_picks[${i}][product_wc_id]`, String(flaps[i + 1]!.wcId))
      })
      extras.forEach((e, i) => {
        const cat = sz.extras.find(c => c.id === e.catId)
        const p = cat?.products.find(x => x.id === e.productId)
        if (!cat || !p?.wcId) return
        fd.append(`extras[${i}][category_id]`, cat.id)
        fd.append(`extras[${i}][category_name]`, cat.trackName)
        fd.append(`extras[${i}][product_wc_id]`, String(p.wcId))
      })

      S.cart = 'loading'
      rerender()
      fetch(opts.wcAjaxUrl, { method: 'POST', body: fd, credentials: 'same-origin' })
        .then(r => r.json())
        .then(data => {
          // Messages du serveur en français : traduits par un message générique dans les autres langues
          if (!data.success) throw new Error(i18n.lang === 'fr' && data.message ? data.message : t('cartError'))
          S.cart = undefined
          onAdded() // écran « Votre lot est dans votre panier »
          // Lot ajouté mais un accessoire épuisé entre-temps : on le dit
          if (data.warning) alert(i18n.lang === 'fr' ? data.warning : t('cartWarning'))
          // Mêmes événements qu'un ajout WooCommerce natif : mini-panier et fragments à jour
          const jq = (window as unknown as { jQuery?: (el: unknown) => { trigger: (e: string) => void } }).jQuery
          if (jq) {
            jq(document.body).trigger('wc_fragment_refresh')
            jq(document.body).trigger('added_to_cart')
          }
        })
        .catch(err => {
          console.error('[FunnelWizard] Ajout au panier :', err)
          S.cart = undefined
          rerender()
          alert(err.message || t('cartError'))
        })
    },
  }

  runWizard(root, wz, DATA, hooks)

  // L'en-tête du site se masque en défilant vers le bas et revient vers le haut : le bandeau des étapes
  // le suit (collé en haut quand il est masqué, juste dessous quand il est visible)
  watchSiteHeader(hdr => {
    const top = root.getElementById('topline') as HTMLElement | null
    wz.style.setProperty('--hdr', hdr + 'px')
    wz.style.setProperty('--top-end', Math.max(hdr, top && !top.hidden ? top.getBoundingClientRect().bottom : 0) + 8 + 'px')
  })
}
